import { Button } from "@mui/material";
import { format, parseISO, isValid } from "date-fns";
import PropTypes from "prop-types";
import React, { useEffect, useState, useCallback } from "react";
import { useLoaderData } from "react-router-dom";

import LoadingIndicator from "../../global/LoadingIndicator";
import ResponseModal from "../../global/ResponseModal";

import AssociateAppointmentsTable from "./AssociateAppointmentsTable";

function AssociateAppointments({
  document,
  setOpenAssociateAppointmentsModal,
  closeUploadModal,
  isLaunchedFromAppointmentsTab,
  selectedAppointment,
  appointments,
  nextPageUrl,
  handleOpenAssociateAppointmentModal,
  onDocumentUpdate,
}) {
  const { patchDocument } = useLoaderData();
  const [appointmentList, setAppointmentList] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const buttonStyle = { width: "120px", height: "40px" };

  //const provider = document && document.provider ? document.provider : "";
  // TODO: Permissions using the new framework
  /*  const canAddAssociatedDocuments = userRoles.permissions.includes(
    "filemanager.add_document_association"
  ); */
  const canAddAssociatedDocuments = true;
  const handleSaveSuccess = () => {
    onDocumentUpdate();
    if (isLaunchedFromAppointmentsTab) {
      closeUploadModal?.();
    } else {
      setOpenAssociateAppointmentsModal?.(false);
    }
  };

  const handleCancel = () => {
    if (isLaunchedFromAppointmentsTab) {
      closeUploadModal?.();
    } else {
      setOpenAssociateAppointmentsModal?.(false);
    }
  };

  const handleSaveFail = () => {
    setOpenAssociateAppointmentsModal?.(false);
  };

  const toDate = (value) => {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === "number") {
      return new Date(value);
    }
    if (typeof value === "string") {
      const d = parseISO(value);
      return isValid(d) ? d : null;
    }
    try {
      const d = new Date(value);
      return isValid(d) ? d : null;
    } catch {
      return null;
    }
  };

  const formatApptDateTime = (appointmentDate, appointmentTime) => {
    let date = "";
    let time = "";

    const d = toDate(appointmentDate);
    if (d && isValid(d)) {
      date = format(d, "MM/dd/yyyy");
    }

    const t = toDate(appointmentTime);
    if (t && isValid(t)) {
      time = format(t, "h:mm a");
    }

    return { date, time };
  };

  const getIncludedPkSet = (doc) => {
    const raw = isLaunchedFromAppointmentsTab ? doc : doc?.appointments;
    const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const set = new Set();
    arr.forEach((it) => {
      if (typeof it === "number") {
        set.add(it);
      } else if (typeof it === "object" && it) {
        if (typeof it.pk === "number") {
          set.add(it.pk);
        } else if (typeof it.id === "number") {
          set.add(it.id);
        }
      }
    });
    return set;
  };

  const formatAppointments = useCallback(
    (list) => {
      const includedSet = getIncludedPkSet(document);

      return (list || []).map((app) => {
        const totalAmount = (app.administeredServices || []).reduce((sum, service) => {
          const n = Number(service?.price ?? 0);
          return sum + (isNaN(n) ? 0 : n);
        }, 0);
        const amount = `$${totalAmount.toFixed(2)}`;

        const { date, time } = formatApptDateTime(app.appointmentDate, app.appointmentTime);

        return {
          id: app.pk,
          date,
          time,
          amount,
          included: includedSet.has(app.pk),
        };
      });
    },
    [document, isLaunchedFromAppointmentsTab]
  );

  const handleSaveData = async () => {
    const idsAssociatedAppointments = appointmentList
      .filter((item) => item.included)
      .map((item) => item.id);

    const documentId = document?.pk ?? document?.id;

    try {
      const successRes = await patchDocument(documentId, {
        appointments: idsAssociatedAppointments,
      });
      if (successRes) {
        setSaveSuccess(true);
      }
    } catch (error) {
      setSaveFailed(true);
    }
  };

  const handleScroll = () => {
    if (nextPageUrl && !isLoading) {
      handleOpenAssociateAppointmentModal?.(document, true, nextPageUrl);
    }
  };

  useEffect(() => {
    let active = true;
    if (active) {
      const allAppointments = formatAppointments(appointments || []);
      let selAppointment = null;

      const selectedPk =
        (selectedAppointment && selectedAppointment.pk) ||
        (Array.isArray(selectedAppointment) && selectedAppointment[0]?.pk) ||
        null;

      if (selectedPk) {
        selAppointment = allAppointments.filter((appt) => appt.id === selectedPk);
      }

      const responseAppointments = isLaunchedFromAppointmentsTab
        ? selAppointment || []
        : allAppointments;

      setAppointmentList((prev) => {
        const map = new Map(prev.map((a) => [a.id, a]));
        (responseAppointments || []).forEach((a) => map.set(a.id, a));
        return Array.from(map.values());
      });

      setIsLoading(false);
    }
    return () => {
      active = false;
    };
  }, [appointments, formatAppointments, isLaunchedFromAppointmentsTab, selectedAppointment]);

  return (
    <>
      {isLoading ? (
        <div className="min-h-[65vh] w-full">
          <LoadingIndicator isLoading={isLoading} />
        </div>
      ) : (
        <>
          <div className="flex w-[100%]">
            {!isLaunchedFromAppointmentsTab && (
              <div className="w-[60%]">
                <div>
                  {document && document.file ? (
                    <object
                      data={document.file}
                      aria-label={document.file_name}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        minHeight: "60vh",
                      }}
                    />
                  ) : (
                    <p
                      style={{
                        fontSize: "1.2rem",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      No File Preview Available
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grow">
              {appointmentList.length > 0 && (
                <AssociateAppointmentsTable
                  next={nextPageUrl}
                  document={document}
                  includedAppointments={
                    isLaunchedFromAppointmentsTab ? document : document?.appointments
                  }
                  appointmentList={appointmentList}
                  setAppointmentList={setAppointmentList}
                  handleScroll={handleScroll}
                  canAddAssociatedDocuments={canAddAssociatedDocuments}
                />
              )}
            </div>
          </div>

          <div>
            {canAddAssociatedDocuments && (
              <div className="space-between mt-6 flex justify-end gap-4">
                <Button
                  style={buttonStyle}
                  variant="contained"
                  color="error"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!appointmentList || appointmentList.length === 0}
                  style={buttonStyle}
                  variant="contained"
                  color="primary"
                  onClick={handleSaveData}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
          <ResponseModal
            title="Appointments successfully associated and saved"
            description="You've updated the Association of Appointments to Documents successfully"
            openBool={saveSuccess}
            setOpenBool={setSaveSuccess}
            handleCloseFunc={handleSaveSuccess}
          />
          <ResponseModal
            title="Can't save your associated appointments"
            isError={true}
            openBool={saveFailed}
            setOpenBool={setSaveFailed}
            errorMessage={null}
            handleCloseFunc={handleSaveFail}
          />
        </>
      )}
    </>
  );
}

AssociateAppointments.propTypes = {
  document: PropTypes.shape({
    pk: PropTypes.number,
    id: PropTypes.number,
    appointments: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ pk: PropTypes.number, id: PropTypes.number }),
      ])
    ),
    file: PropTypes.string,
    file_name: PropTypes.string,
    provider: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setOpenAssociateAppointmentsModal: PropTypes.func,
  closeUploadModal: PropTypes.func,
  isLaunchedFromAppointmentsTab: PropTypes.bool,
  selectedAppointment: PropTypes.oneOfType([
    PropTypes.shape({ pk: PropTypes.number }),
    PropTypes.arrayOf(PropTypes.shape({ pk: PropTypes.number })),
  ]),
  appointments: PropTypes.arrayOf(PropTypes.object),
  nextPageUrl: PropTypes.string,
  handleOpenAssociateAppointmentModal: PropTypes.func,
};

export default AssociateAppointments;
